export const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso).slice(0, 10);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const MONTHS = ["ian", "feb", "mar", "apr", "mai", "iun", "iul", "aug", "sep", "oct", "nov", "dec"];
const MONTHS_LONG = ["ianuarie", "februarie", "martie", "aprilie", "mai", "iunie", "iulie", "august", "septembrie", "octombrie", "noiembrie", "decembrie"];

/** 14/03/2026 */
export const fmtDateHuman = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso).slice(0, 10);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

/** 06/09/2026 */
export const fmtDateLong = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso).slice(0, 10);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

/** 10:30 */
export const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/** { day: "24", mon: "SEPT." } pentru badge-uri de dată */
export const dateBadge = (iso) => {
  if (!iso) return { day: "—", mon: "" };
  const d = new Date(iso);
  if (isNaN(d)) return { day: "—", mon: "" };
  return { day: String(d.getDate()), mon: `${MONTHS[d.getMonth()].toUpperCase()}.` };
};

/** 14/03/2026, 10:30 */
export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso).replace("T", " ").slice(0, 16);
  return `${fmtDateHuman(iso)}, ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/** "2026-03" → "martie 2026" */
export const fmtMonth = (ym) => {
  if (!ym) return "";
  const [y, m] = String(ym).split("-");
  const idx = parseInt(m, 10) - 1;
  const name = MONTHS_LONG[idx];
  return name ? `${name[0].toUpperCase()}${name.slice(1)} ${y}` : ym;
};

export const timeAgo = (iso) => {
  if (!iso) return "";
  const secs = (Date.now() - new Date(iso).getTime()) / 1000;
  if (secs < 60) return "acum";
  if (secs < 3600) return `${Math.floor(secs / 60)} min`;
  if (secs < 86400) return `acum ${Math.floor(secs / 3600)} h`;
  const days = Math.floor(secs / 86400);
  if (days === 1) return "ieri";
  if (days < 7) return `acum ${days} zile`;
  return fmtDateHuman(iso);
};

/** zile până la deadline (negativ = depășit) */
export const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00");
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((d - t) / 86400000);
};

export const deadlineLabel = (iso) => {
  const n = daysUntil(iso);
  if (n === null) return null;
  if (n < 0) return { text: `Depășit cu ${-n} ${-n === 1 ? "zi" : "zile"}`, tone: "danger" };
  if (n === 0) return { text: "Termen astăzi", tone: "danger" };
  if (n <= 3) return { text: `${n} ${n === 1 ? "zi" : "zile"} rămase`, tone: "warning" };
  return { text: `Termen ${fmtDateHuman(iso)}`, tone: "neutral" };
};

export const initials = (name = "") =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join("") || "?";

export const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Bună dimineața";
  if (h < 18) return "Bună ziua";
  return "Bună seara";
};

export const pluralize = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** „În 3 luni” / „În 1 an și 11 luni” / „În 12 zile” / „Depășit cu 4 zile” */
export const untilLabel = (iso) => {
  const n = daysUntil(iso);
  if (n === null) return "";
  if (n < 0) return `Depășit cu ${pluralize(-n, "zi", "zile")}`;
  if (n === 0) return "Astăzi";
  if (n < 31) return `În ${pluralize(n, "zi", "zile")}`;
  const months = Math.round(n / 30.44);
  if (months < 12) return `În ${pluralize(months, "lună", "luni")}`;
  const y = Math.floor(months / 12), m = months % 12;
  return `În ${pluralize(y, "an", "ani")}${m ? ` și ${pluralize(m, "lună", "luni")}` : ""}`;
};

const CURRENCY_SYMBOL = { EUR: "€", RON: "lei", USD: "$", GBP: "£" };
/** 24812000 → „24.812.000 €” */
export const fmtMoney = (n, currency = "EUR") => {
  if (n === null || n === undefined || isNaN(Number(n))) return "—";
  const s = new Intl.NumberFormat("ro-RO", { maximumFractionDigits: 0 }).format(Number(n));
  const cur = String(currency || "EUR").toUpperCase();
  return `${s} ${CURRENCY_SYMBOL[cur] || cur}`;
};

/** 1234567 → „1,2 MB” */
export const fmtSize = (bytes) => {
  const b = Number(bytes) || 0;
  if (b <= 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
};
